import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Form } from "react-bootstrap";
import AttorneyQualifications, {
  emptyQualifications,
} from "../Reviews/AttorneyQualifications";
import { apiBase } from "../Reviews/api";
import "../Reviews/reviews.css";

/**
 * Public attorney application. Submitting sends the details to the Reviewed
 * team for review; it does not create an account. Accepted attorneys receive
 * an email to set their password.
 */
export default function WorkWithUs() {
  const [contact, setContact] = useState({
    fullName: "",
    email: "",
    phone: "",
    website: "",
    company: "",
  });
  const [form, setForm] = useState<any>(emptyQualifications());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const setField = (key: keyof typeof contact, value: string) =>
    setContact(c => ({ ...c, [key]: value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/api/attorney-applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: contact.fullName,
          email: contact.email,
          ...(contact.phone.trim() ? { phone: contact.phone } : {}),
          ...(contact.website.trim() ? { website: contact.website } : {}),
          ...(contact.company ? { company: contact.company } : {}),
          profile: {
            ...form,
            displayName: form.displayName.trim() || contact.fullName,
            languages: form.languages.filter((l: string) => l.trim()),
          },
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(
          result.message || result.error || "Could not send your application."
        );
      setDone(result.message);
      window.scrollTo(0, 0);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (done)
    return (
      <main className="reviewed-documents review-page">
        <header className="reviewed-documents-heading mt-4">
          <div>
            <p className="reviewed-eyebrow">WORK WITH US</p>
            <h1>Application received</h1>
            <p>{done}</p>
          </div>
        </header>
        <Link to="/login">← Back to sign in</Link>
      </main>
    );

  return (
    <main className="reviewed-documents review-page">
      <Link to="/login">← Sign in</Link>
      <header className="reviewed-documents-heading mt-4">
        <div>
          <p className="reviewed-eyebrow">WORK WITH US</p>
          <h1>Review questions as a Reviewed attorney</h1>
          <p>
            Clients bring a question they explored with AI, and a qualified
            attorney gives them a written answer on agreed scope, price, and
            turnaround. Tell us about your practice. Our team reviews every
            application and verifies admissions before creating your account.
          </p>
        </div>
      </header>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={submit}>
        <fieldset disabled={busy}>
          <section className="review-section">
            <h2>Contact</h2>
            <div className="review-form-grid">
              <Form.Group controlId="apply-name">
                <Form.Label>Full name</Form.Label>
                <Form.Control
                  required
                  maxLength={200}
                  autoComplete="name"
                  value={contact.fullName}
                  onChange={e => setField("fullName", e.target.value)}
                />
              </Form.Group>
              <Form.Group controlId="apply-email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  required
                  type="email"
                  maxLength={100}
                  autoComplete="email"
                  value={contact.email}
                  onChange={e => setField("email", e.target.value)}
                />
              </Form.Group>
              <Form.Group controlId="apply-phone">
                <Form.Label>Phone (optional)</Form.Label>
                <Form.Control
                  maxLength={40}
                  autoComplete="tel"
                  value={contact.phone}
                  onChange={e => setField("phone", e.target.value)}
                />
              </Form.Group>
              <Form.Group controlId="apply-website">
                <Form.Label>Website or LinkedIn (optional)</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://"
                  maxLength={300}
                  value={contact.website}
                  onChange={e => setField("website", e.target.value)}
                />
              </Form.Group>
            </div>
            {/* Hidden from people; bots that fill every field are rejected. */}
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="d-none"
              value={contact.company}
              onChange={e => setField("company", e.target.value)}
            />
          </section>
          <AttorneyQualifications
            form={form}
            onChange={(key, value) =>
              setForm((f: any) => ({ ...f, [key]: value }))
            }
          />
          <p className="text-secondary">
            Submitting sends these details to the Reviewed team. It does not
            create an account or an attorney–client relationship.
          </p>
          <Button type="submit" disabled={busy}>
            {busy ? "Sending…" : "Submit application"}
          </Button>
        </fieldset>
      </Form>
    </main>
  );
}
